import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { getColorAndLabelForPowderyMildewValue, isEmpty, isNil } from '@semios/app-platform-common'
import type { TValuesCurrentBlocks, TValuesCurrentProperties } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import type { TRGBAColorWith1AtTheEnd } from 'settings/colors'
import { colors } from 'settings/colors'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { apiFetch } from 'utils/apiFetch'
import { doesBlockHaveValueType } from 'utils/doesFieldAssetHaveValueType'
import type { TGetCacheUpdatesFromResponseParameters, TGetCacheUpdatesFromResponseReturn } from './_types'
import { generateUsualStyleAPIArgs } from './_utils/generateUsualStyleAPIArgs'
import { populateSCDsForBlocks } from './_utils/generateUsualStyleGetCacheUpdatesFromResponse'
import { getValueType } from './_utils/getValueType'

const heatmapExtremesForAllPropertiesInVisibleRegions = false
const valueGroup = 'grape_powdery_mildew'

const makeApiArgs = (
  processedCaches: TGetCacheUpdatesFromResponseParameters['processedCaches'],
): routes.ValuesCurrent.Request => {
  return generateUsualStyleAPIArgs({
    heatmapExtremesForAllPropertiesInVisibleRegions,
    processedCaches,
    block: {
      valueType: getValueType(MAP_VISUAL.BLOCK, valueGroup),
    },
    property: {
      valueType: getValueType(MAP_VISUAL.PROPERTY, valueGroup),
    },
  })
}

const getHeatmapCSS = (color: TRGBAColorWith1AtTheEnd | null) => {
  if (color) {
    return {
      backgroundColor: color,
      color: colors.midnight,
    }
  }

  return {
    backgroundColor: colors.midnight,
    color: colors.white,
  }
}

export const getResponseAndShapeForCacheUpdate = async ({
  cacheKeys,
  processedCaches: processedCachesArgs,
}: TGetCacheUpdatesFromResponseParameters): Promise<TGetCacheUpdatesFromResponseReturn> => {
  const args = makeApiArgs(processedCachesArgs)

  if (isEmpty(args)) return {}

  const response = await apiFetch({ url: '/values-current', body: args })
  const processedCaches = populateSCDsForBlocks(response, processedCachesArgs)
  const returner: TGetCacheUpdatesFromResponseReturn = {}
  const blockValueType = getValueType(MAP_VISUAL.BLOCK, valueGroup) as TValuesCurrentBlocks

  if (!!processedCaches.blockValues.itemsWithinView.length && blockValueType) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues = processedCaches.blockValues.itemsWithinView.flatMap((block) => {
      const valueObj = response?.blocks?.[block.id]?.[blockValueType]

      let val: number | null = null

      if (valueObj && 'value' in valueObj) {
        val = valueObj.value as number | null
      }

      const { value, color } = getColorAndLabelForPowderyMildewValue(val)

      const blockCouldHaveValue = doesBlockHaveValueType({
        propertyId: block.meta.propertyId,
        blockId: Number(block.id),
        currentValueToCheck: blockValueType,
      })

      if (isNil(value) && !blockCouldHaveValue) {
        return []
      }

      itemIdsWithinView.push(block.id)

      return {
        id: String(block.meta.blockId),
        value: {
          [blockValueType]: {
            children: value ? translate.phrases.banyanApp(value) : translate.phrases.templates('-'),
            getContainerCSS: () => getHeatmapCSS(color),
          },
        },
      }
    })

    returner.blocks = {
      itemsWithinViewThatNowHaveValues,
      itemIdsWithinView,
      cacheKey: cacheKeys.blockValuesCacheKey,
    }
  }

  const willShowStations = !!returner.stations?.itemIdsWithinView.length
  const willShowBlocks = !!returner.blocks?.itemIdsWithinView.length
  const willShowHeatmapPoints = !!returner.heatmapPoints?.itemIdsWithinView.length
  const propertyIdsToValues: Record<string, number | null> = {}
  const propertyIds = processedCaches.propertyValues.itemsWithinView.map((s) => Number(s.id))
  const propertyValueType = getValueType(MAP_VISUAL.PROPERTY, valueGroup) as TValuesCurrentProperties

  if (propertyValueType) {
    propertyIds.forEach((propertyId) => {
      const valueObj = response?.properties?.[propertyId]?.[propertyValueType]

      let val: number | null = null

      if (valueObj && 'value' in valueObj) {
        val = valueObj.value as number | null
      }

      propertyIdsToValues[propertyId] = val
    })
  }

  const showValuesForPropertyRatherThanName =
    !isEmpty(propertyIdsToValues) && !willShowStations && !willShowBlocks && !willShowHeatmapPoints

  const propertyItemsWithinViewThatNowHaveValues = processedCaches.propertyValues.itemsWithinView.map(
    (property) => {
      if (!showValuesForPropertyRatherThanName) {
        return {
          id: String(property.id),
          value: {
            [String(propertyValueType)]: {
              children: property.meta.propertyName,
              onHover: false,
              getContainerCSS: () => ({ backgroundColor: colors.midnight, color: 'white' }),
              baseZIndex: undefined,
            },
          },
        }
      }

      const val = propertyIdsToValues[property.id]
      const { value, color } = getColorAndLabelForPowderyMildewValue(val)

      return {
        id: String(property.id),
        value: {
          [String(propertyValueType)]: {
            children: value ? translate.phrases.banyanApp(value) : translate.phrases.templates('-'),
            getContainerCSS: () => getHeatmapCSS(color),
            onHover: true,
            baseZIndex: isNil(value) ? -10 : undefined,
          },
        },
      }
    },
  )

  returner.properties = {
    itemsWithinViewThatNowHaveValues: propertyItemsWithinViewThatNowHaveValues,
    itemIdsWithinView: propertyItemsWithinViewThatNowHaveValues.map((s) => s.id),
    cacheKey: cacheKeys.propertyCacheKey,
  }

  return returner
}
